// components/Survey/SurveyManagerPage.jsx — admin list of surveys with KPIs, templates and actions
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider, Text, Tooltip,
} from "@fluentui/react-components";
import {
  Add24Regular, ClipboardTaskListLtr24Regular, DataPie24Regular, Share24Regular, Edit24Regular,
  MoreHorizontal24Regular, Copy24Regular, Archive24Regular, Play24Regular, Delete24Regular,
  Search24Regular, People24Regular, CheckmarkCircle24Filled, Gauge24Regular, LockClosed24Regular,
  Sparkle24Regular, Clock24Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useSurveys } from "../../context/SurveyContext";
import {
  SURVEY_TEMPLATES, countQuestions, estimateMinutes, selectResponses, relativeTime, formatDate, isSurveyOpen,
} from "./surveyUtils";
import {
  C, card, StatusChip, KpiTile, Meter, EmptyState, LoadingBlock, ErrorBanner, useToast, ConfirmDialog,
} from "./surveyUI";
import ShareSurveyDialog from "./ShareSurveyDialog";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "draft", label: "Drafts" },
  { key: "closed", label: "Closed" },
];

export function TemplatePicker({ open, onClose }) {
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface style={{ maxWidth: 760, width: "92vw" }}>
        <DialogBody>
          <DialogTitle>Create a survey</DialogTitle>
          <DialogContent>
            <Text style={{ fontSize: 13, color: C.ink2, display: "block", marginBottom: 16 }}>
              Start blank or from a ready-made template. Everything can be edited afterwards.
            </Text>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 12 }}>
              {SURVEY_TEMPLATES.map((t) => {
                const preview = t.build();
                const n = countQuestions(preview.questions);
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => { onClose(); navigate(`/surveys/new?template=${t.key}`); }}
                    style={{
                      textAlign: "left", padding: "16px 16px 14px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
                      border: `1.5px solid ${t.key === "blank" ? "#C7DBFF" : C.line}`,
                      background: t.key === "blank" ? "#F0F6FF" : "#fff", transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,82,204,0.12)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.key === "blank" ? "#C7DBFF" : C.line; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <span style={{ width: 36, height: 36, borderRadius: 10, background: t.key === "blank" ? C.brand : "#EEF4FF", color: t.key === "blank" ? "#fff" : C.brand, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                      {t.key === "blank" ? <Add24Regular /> : <Sparkle24Regular />}
                    </span>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.ink }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: C.ink2, marginTop: 2, lineHeight: 1.5 }}>{t.desc}</div>
                    {t.key !== "blank" && <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{n} questions · ~{estimateMinutes(preview.questions)} min</div>}
                  </button>
                );
              })}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default function SurveyManagerPage() {
  const navigate = useNavigate();
  const { isAdmin } = useUserAccess();
  const { surveys, assignments, responses, loading, error, refresh, duplicateSurvey, setSurveyStatus, deleteSurvey } = useSurveys();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, showToast] = useToast();

  // Per-survey participation stats
  const stats = useMemo(() => {
    const out = {};
    surveys.forEach((s) => {
      const as = assignments.filter((a) => a.surveyId === s.id);
      const rs = responses.filter((r) => r.surveyId === s.id);
      const invited = new Set(as.map((a) => a.email)).size;
      const responded = new Set(rs.map((r) => r.email)).size;
      const latest = rs.reduce((m, r) => (r.submittedAt > m ? r.submittedAt : m), "");
      out[s.id] = { invited, responded, submissions: rs.length, rate: invited ? (responded / invited) * 100 : 0, latest, pending: as.filter((a) => !rs.some((r) => r.assignmentId === a.id)).length };
    });
    return out;
  }, [surveys, assignments, responses]);

  const totals = useMemo(() => {
    const active = surveys.filter((s) => s.status === "active").length;
    const withInvites = surveys.filter((s) => stats[s.id]?.invited);
    const avgRate = withInvites.length ? withInvites.reduce((n, s) => n + stats[s.id].rate, 0) / withInvites.length : 0;
    const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString();
    return {
      active,
      drafts: surveys.filter((s) => s.status === "draft").length,
      responses: selectResponses(responses, { mode: "all" }).length,
      recent: responses.filter((r) => r.submittedAt >= monthAgo).length,
      avgRate,
      pending: Object.values(stats).reduce((n, s) => n + s.pending, 0),
    };
  }, [surveys, responses, stats]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return surveys
      .filter((s) => filter === "all" || s.status === filter)
      .filter((s) => !q || s.title.toLowerCase().includes(q) || (s.category || "").toLowerCase().includes(q))
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }, [surveys, filter, search]);

  if (!isAdmin) {
    return <EmptyState icon={LockClosed24Regular} title="Access restricted" text="Only administrators can create and manage staff surveys." />;
  }

  const act = async (fn, msg) => {
    try { await fn(); showToast(msg); } catch (err) { showToast(err.message, "error"); }
  };

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <Text style={{ fontSize: 24, fontWeight: 800, color: C.navy, display: "block" }}>Staff Surveys</Text>
          <Text style={{ fontSize: 13, color: C.ink2 }}>Design surveys, share them with staff and analyse the results.</Text>
        </div>
        <Button appearance="primary" size="large" icon={<Add24Regular />} onClick={() => setPickerOpen(true)} style={{ background: C.brand }}>
          New survey
        </Button>
      </div>

      <ErrorBanner error={error} onRetry={refresh} />

      {/* KPIs */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <KpiTile icon={ClipboardTaskListLtr24Regular} label="Active surveys" value={totals.active} sub={`${totals.drafts} draft${totals.drafts === 1 ? "" : "s"}`} />
        <KpiTile icon={CheckmarkCircle24Filled} label="Responses" value={totals.responses} sub={`${totals.recent} in the last 30 days`} color="#0B6A0B" />
        <KpiTile icon={Gauge24Regular} label="Average response rate" value={`${Math.round(totals.avgRate)}%`} color="#5B3DC4">
          <Meter pct={totals.avgRate} color="#5B3DC4" />
        </KpiTile>
        <KpiTile icon={Clock24Regular} label="Awaiting response" value={totals.pending} sub="Open invitations" color="#B8430A" />
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 4, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 10, padding: 3 }}>
          {FILTERS.map((f) => {
            const n = f.key === "all" ? surveys.length : surveys.filter((s) => s.status === f.key).length;
            const on = filter === f.key;
            return (
              <button key={f.key} type="button" onClick={() => setFilter(f.key)} style={{ border: "none", cursor: "pointer", fontFamily: "inherit", padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: on ? 700 : 500, background: on ? C.navy2 : "transparent", color: on ? "#fff" : C.ink2 }}>
                {f.label} <span style={{ opacity: 0.7, marginLeft: 2 }}>{n}</span>
              </button>
            );
          })}
        </div>
        <div style={{ position: "relative", flex: 1, minWidth: 220, maxWidth: 360, marginLeft: "auto" }}>
          <Search24Regular style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: C.muted }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search surveys…" style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13, outline: "none" }} />
        </div>
      </div>

      {/* List */}
      {loading ? <LoadingBlock /> : visible.length === 0 ? (
        surveys.length === 0 ? (
          <EmptyState
            icon={ClipboardTaskListLtr24Regular}
            title="No surveys yet"
            text="Create your first staff survey — start from a template such as Employee Engagement or HSE Pulse Check, or build one from scratch."
            action={<Button appearance="primary" icon={<Add24Regular />} onClick={() => setPickerOpen(true)}>Create survey</Button>}
          />
        ) : <EmptyState icon={Search24Regular} title="No matching surveys" text="Try a different filter or search term." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visible.map((s) => {
            const st = stats[s.id] || {};
            const status = s.status === "active" && !isSurveyOpen(s) ? "closed" : s.status;
            return (
              <div key={s.id} style={{ ...card, padding: "16px 20px", display: "grid", gridTemplateColumns: "minmax(260px, 2.2fr) minmax(200px, 1.2fr) auto", gap: 20, alignItems: "center" }}>
                <div style={{ minWidth: 0, cursor: "pointer" }} onClick={() => navigate(st.submissions ? `/surveys/${s.id}/results` : `/surveys/${s.id}/edit`)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                    <Text style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{s.title}</Text>
                    <StatusChip status={status} label={s.status === "active" && status === "closed" ? "Past close date" : undefined} />
                    {s.settings?.anonymous && <span style={{ fontSize: 11, fontWeight: 600, color: "#5B3DC4", background: "#F1EDFF", padding: "2px 8px", borderRadius: 20 }}>Anonymous</span>}
                    {s.category && <span style={{ fontSize: 11, color: C.ink2, background: C.page, padding: "2px 8px", borderRadius: 20 }}>{s.category}</span>}
                  </div>
                  <Text style={{ fontSize: 12, color: C.muted, display: "block" }}>
                    {countQuestions(s.questions)} questions · ~{estimateMinutes(s.questions)} min · updated {relativeTime(s.updatedAt)}
                    {s.settings?.closesAt ? ` · closes ${formatDate(s.settings.closesAt)}` : ""}
                  </Text>
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.ink2, marginBottom: 6 }}>
                    <span><strong style={{ color: C.ink }}>{st.responded || 0}</strong> of {st.invited || 0} responded</span>
                    <span style={{ fontWeight: 700, color: C.ink }}>{st.invited ? `${Math.round(st.rate)}%` : "—"}</span>
                  </div>
                  <Meter pct={st.rate} color={st.rate >= 70 ? "#2E8540" : st.rate >= 40 ? C.brand : "#B8430A"} />
                  <Text style={{ fontSize: 11, color: C.muted, display: "block", marginTop: 6 }}>
                    {st.latest ? `Last response ${relativeTime(st.latest)}` : st.invited ? "No responses yet" : "Not shared yet"}
                  </Text>
                </div>

                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <Tooltip content="Results & analysis" relationship="label">
                    <Button appearance="subtle" icon={<DataPie24Regular />} onClick={() => navigate(`/surveys/${s.id}/results`)} />
                  </Tooltip>
                  <Tooltip content="Edit questions" relationship="label">
                    <Button appearance="subtle" icon={<Edit24Regular />} onClick={() => navigate(`/surveys/${s.id}/edit`)} />
                  </Tooltip>
                  <Button appearance="primary" icon={<Share24Regular />} onClick={() => setShareId(s.id)} disabled={s.status === "closed"}>
                    Share
                  </Button>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button appearance="subtle" icon={<MoreHorizontal24Regular />} aria-label="More actions" />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem icon={<People24Regular />} onClick={() => navigate(`/surveys/${s.id}/results?tab=participation`)}>Participation</MenuItem>
                        <MenuItem icon={<Copy24Regular />} onClick={() => act(() => duplicateSurvey(s.id), "Survey duplicated as a draft")}>Duplicate</MenuItem>
                        {s.status === "active"
                          ? <MenuItem icon={<Archive24Regular />} onClick={() => act(() => setSurveyStatus(s.id, "closed"), "Survey closed — staff can no longer respond")}>Close survey</MenuItem>
                          : <MenuItem icon={<Play24Regular />} onClick={() => act(() => setSurveyStatus(s.id, "active"), s.status === "draft" ? "Survey published" : "Survey reopened")}>{s.status === "draft" ? "Publish" : "Reopen"}</MenuItem>}
                        <MenuDivider />
                        <MenuItem icon={<Delete24Regular />} onClick={() => setDeleteTarget(s)} style={{ color: C.danger }}>Delete</MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TemplatePicker open={pickerOpen} onClose={() => setPickerOpen(false)} />

      {shareId && (
        <ShareSurveyDialog surveyId={shareId} onClose={() => setShareId(null)} onDone={(msg, type) => showToast(msg, type)} />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete survey?"
        danger
        busy={deleting}
        confirmLabel="Delete permanently"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await deleteSurvey(deleteTarget.id);
            showToast("Survey deleted");
            setDeleteTarget(null);
          } catch (err) {
            showToast(err.message, "error");
          } finally {
            setDeleting(false);
          }
        }}
      >
        <strong>{deleteTarget?.title}</strong> will be removed along with its {stats[deleteTarget?.id]?.invited || 0} invitation(s) and {stats[deleteTarget?.id]?.submissions || 0} response(s). This cannot be undone.
        {stats[deleteTarget?.id]?.submissions > 0 && <div style={{ marginTop: 8 }}>Tip: close the survey instead to keep its results.</div>}
      </ConfirmDialog>

      {toast}
    </div>
  );
}
